/**
 * SVG 파일의 Content-Type 문제를 해결하는 프록시 함수
 */
const createSvgProxy = async (svgUrl: string): Promise<string> => {
  try {
    const response = await fetch(svgUrl);
    const svgText = await response.text();
    
    // SVG를 data URL로 변환
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
    return URL.createObjectURL(svgBlob);
  } catch (error) {
    /// console.error('SVG 프록시 생성 실패:', error);
    throw error;
  }
};

/**
 * 파비콘을 동적으로 업데이트하는 함수 (Azure Blob SVG 문제 해결)
 */
export const updateFavicon = async (faviconUrl: string) => {
  try {
    // 기존 파비콘 제거
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach(link => link.remove());

    // /// console.log('파비콘 업데이트 시작:', faviconUrl);

    let finalFaviconUrl = faviconUrl;

    // SVG 파일이고 Azure Blob Storage인 경우 프록시 사용
    if (faviconUrl.toLowerCase().includes('.svg') && 
        faviconUrl.includes('blob.core.windows.net')) {
      /// console.log('Azure Blob SVG 감지, 프록시 생성 중...');
      
      try {
        finalFaviconUrl = await createSvgProxy(faviconUrl);
        /// console.log('SVG 프록시 생성 완료:', finalFaviconUrl);
      } catch (error) {
        /// console.warn('SVG 프록시 실패, 원본 URL 사용:', error);
        finalFaviconUrl = faviconUrl;
      }
    }

    // 캐시 버스터 (프록시가 아닌 경우만)
    if (!finalFaviconUrl.startsWith('blob:')) {
      const timestamp = new Date().getTime();
      finalFaviconUrl = `${finalFaviconUrl}?t=${timestamp}`;
    }

    // 파비콘 추가
    const favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.href = finalFaviconUrl;
    
    if (faviconUrl.toLowerCase().includes('.svg')) {
      favicon.type = 'image/svg+xml';
    }
    
    document.head.appendChild(favicon);

    // 추가 호환성
    const shortcutFavicon = document.createElement('link');
    shortcutFavicon.rel = 'shortcut icon';
    shortcutFavicon.href = finalFaviconUrl;
    document.head.appendChild(shortcutFavicon);

    // Apple Touch Icon
    const appleTouchIcon = document.createElement('link');
    appleTouchIcon.rel = 'apple-touch-icon';
    appleTouchIcon.href = finalFaviconUrl;
    document.head.appendChild(appleTouchIcon);

    // /// console.log('파비콘 업데이트 완료:', finalFaviconUrl);

    // 브라우저 강제 새로고침
    forceFaviconRefresh();

  } catch (error) {
    /// console.error('파비콘 업데이트 실패:', error);
    
    // 실패 시 기본 파비콘
    const defaultFavicon = document.createElement('link');
    defaultFavicon.rel = 'icon';
    defaultFavicon.href = '/favicon.ico';
    document.head.appendChild(defaultFavicon);
  }
};

/**
 * 브라우저가 파비콘 변경을 강제로 인식하도록 하는 함수
 */
const forceFaviconRefresh = () => {
  // 제목 임시 변경
  const originalTitle = document.title;
  document.title = originalTitle + ' ';
  setTimeout(() => {
    document.title = originalTitle;
  }, 100);

  // 임시 파비콘 삽입
  const tempFavicon = document.createElement('link');
  tempFavicon.rel = 'icon';
  tempFavicon.href = 'data:image/x-icon;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQEAYAAABPYyMiAAAABmJLR0T///////8JWPfcAAAACXBIWXMAAABIAAAASABGyWs+AAAAF0lEQVRIx2NgGAWjYBSMglEwCkbBSAcACAAXAAGn0+gCAAAAAElFTkSuQmCC';
  document.head.appendChild(tempFavicon);
  
  setTimeout(() => {
    tempFavicon.remove();
  }, 50);
};

/**
 * 웹사이트 제목을 동적으로 업데이트하는 함수
 */
export const updateWebTitle = (webName: string) => {
  document.title = webName;
  
  // meta 태그도 업데이트
  const metaTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement;
  if (metaTitle) {
    metaTitle.content = webName;
  }
  
  const metaSiteName = document.querySelector('meta[property="og:site_name"]') as HTMLMetaElement;
  if (metaSiteName) {
    metaSiteName.content = webName;
  }
};

/**
 * 기본 UI 설정을 적용하는 함수
 */
export const applyBasicUiSettings = async (uiData: {
  favicon_url: string;
  web_name: string;
}) => {
  await updateFavicon(uiData.favicon_url);
  updateWebTitle(uiData.web_name);
};